import React, { useState, useEffect } from 'react';
import { Message, MessageContent, ThinkingState } from '../types';
import MarkdownRenderer from './MarkdownRenderer';
import { formatTimestamp } from '../utils/date';
import ReasoningProcess from './ReasoningProcess';
import { modelHasCapability } from '../models';
import SearchingProcess from './SearchingProcess';
import { cleanRedactionBlocks } from '../utils/text';

interface ChatMessageProps {
    message: Message;
    isStreaming?: boolean;
    modelId?: string; // Needed for reasoning capability check
    onEditRequest: (messageId: string) => void; // Callback to request editing the PREVIOUS user message
    onRegenerateRequest: (messageId: string) => void; // Callback to regenerate this assistant message
    onCopyResponse: (content: string | MessageContent[]) => void; // Callback to copy this message content
}

const ChatMessage: React.FC<ChatMessageProps> = ({
    message,
    isStreaming = false,
    modelId = '',
    onEditRequest,
    onRegenerateRequest,
    onCopyResponse
}) => {
    const isUser = message.role === 'user';
    const [thinkingState, setThinkingState] = useState<ThinkingState>('none');
    const [thinkingTime, setThinkingTime] = useState(0);
    const isSearching = message.isSearching === true;

    // Show thinking animation based on isThinking flag and reasoning capability
    useEffect(() => {
        if (isUser) {
            setThinkingState('none');
            return;
        }

        if (message.isThinking === false && message.thinking) {
            if (message.thinkingStartTime && message.thinkingEndTime) {
                const actualThinkingTime = (message.thinkingEndTime - message.thinkingStartTime) / 1000;
                setThinkingTime(Math.max(1.5, actualThinkingTime));
            } else {
                setThinkingTime(2.5);
            }
            setThinkingState('hidden');
        } else if (message.isThinking === true) {
            setThinkingState('hidden'); // Ensure it starts hidden even if live
        } else if (message.thinking && modelId && modelHasCapability(modelId, 'reasoning')) {
            if (message.thinkingStartTime && message.thinkingEndTime) {
                const actualThinkingTime = (message.thinkingEndTime - message.thinkingStartTime) / 1000;
                setThinkingTime(Math.max(1.5, actualThinkingTime));
            } else {
                setThinkingTime(2.5);
            }
            setThinkingState('hidden');
        } else {
            setThinkingState('none');
        }
    }, [isUser, message.thinking, message.isThinking, message.thinkingStartTime, message.thinkingEndTime, modelId]);

    const toggleThinking = () => {
        if (thinkingState === 'hidden') {
            setThinkingState('visible');
        } else if (thinkingState === 'visible') {
            setThinkingState('hidden');
        }
    };

    // Helper to extract plain text for copying
    const getPlainTextContent = (): string => {
        if (typeof message.content === 'string') {
            // Clean any redaction blocks before providing for copy
            return cleanRedactionBlocks(message.content);
        } else if (Array.isArray(message.content)) {
            return message.content
                .filter(item => item.type === 'text') // Only consider text items
                .map(item => item.type === 'text' ? cleanRedactionBlocks(item.text || '') : '') // Clean and safely access text
                .join('\n\n'); // Add spacing between text parts
        }
        return '';
    };

    const renderContent = () => {
        if (typeof message.content === 'string') {
            // Clean redaction blocks before passing to MarkdownRenderer
            const cleanedContent = cleanRedactionBlocks(message.content);
            return <MarkdownRenderer content={cleanedContent} />;
        } else if (Array.isArray(message.content)) {
            return (
                <div className="space-y-4">
                    {message.content.map((item: MessageContent, index: number) => {
                        if (item.type === 'text' && item.text) {
                            const fileEmbedRegex = /^\[(.*?) - (.*?)\]$/;
                            const match = item.text.match(fileEmbedRegex);
                            if (match) {
                                const [_, fileName, fileType] = match;
                                return (
                                    <div key={index} className="flex items-center bg-background-secondary border border-glass-border rounded-lg px-3 py-2 text-sm my-2 w-fit">
                                        <div className="flex items-center">
                                            <div className="bg-background-tertiary rounded-md p-2 mr-2">
                                                {fileType.toLowerCase().includes('json') ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                        <polyline points="14 2 14 8 20 8" />
                                                        <path d="M10 12a1 1 0 0 0-1 1v1a1 1 0 0 1-1 1 1 1 0 0 1 1 1v1a1 1 0 0 0 1 1" />
                                                        <path d="M14 18a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1 1 1 0 0 1-1-1v-1a1 1 0 0 0-1-1" />
                                                    </svg>
                                                ) : fileType.toLowerCase().includes('text') ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                        <polyline points="14 2 14 8 20 8" />
                                                        <line x1="16" y1="13" x2="8" y2="13" />
                                                        <line x1="16" y1="17" x2="8" y2="17" />
                                                        <polyline points="10 9 9 9 8 9" />
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                                                        <polyline points="13 2 13 9 20 9" />
                                                    </svg>
                                                )}
                                            </div>
                                            <div>
                                                <div className="font-medium">{fileName}</div>
                                                <div className="text-xs text-gray-400">{fileType}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            // Clean redaction blocks before rendering markdown
                            const cleanedText = cleanRedactionBlocks(item.text);
                            return (
                                <div key={index}>
                                    <MarkdownRenderer content={cleanedText} />
                                </div>
                            );
                        } else if (item.type === 'image_url' && item.image_url?.url) {
                            return (
                                <div key={index} className="my-2">
                                    <img
                                        src={item.image_url.url}
                                        alt="Uploaded image"
                                        className="max-w-full max-h-[300px] rounded-md"
                                    />
                                </div>
                            );
                        } else if (item.type === 'file_content') {
                            return null;
                        }
                        return null;
                    })}
                </div>
            );
        }
        return null;
    };

    return (
        <div className={`flex ${message.role === "user" ? "justify-end" : "justify-start"} py-2 group`}>
            <div
                className={`relative rounded-lg px-4 py-2 max-w-[80%] ${message.role === "user"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 dark:bg-[#252525] border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                    }`}
            >
                <div className="text-xs text-gray-400 dark:text-gray-400 mb-1">
                    {message.role === "user" ? 'You' : 'Versetaille'} • {formatTimestamp(message.timestamp)}
                </div>

                {/* Search process display - only show when searching AND not streaming */}
                {message.role !== "user" && isSearching && !isStreaming && (
                    <SearchingProcess isSearching={true} />
                )}

                {/* Reasoning process display */}
                {message.role !== "user" && !isSearching && thinkingState !== 'none' && (message.thinking || message.isThinking) && (
                    <ReasoningProcess
                        thinking={message.thinking || ''}
                        thinkingState={thinkingState}
                        onToggleThinking={toggleThinking}
                        thinkingTime={thinkingTime}
                        isLive={message.isThinking}
                    />
                )}

                {/* Main Message Content - show if not searching or if streaming is active */}
                <div className="max-w-[clamp(20ch,80ch,80vw)]">
                    {(!isSearching || isStreaming) && renderContent()}
                </div>

                {/* Action Buttons for User Messages (not streaming) */}
                {message.role === 'user' && !isStreaming && (
                    <div className="absolute -bottom-2 right-2 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-blue-600 p-1 rounded-md shadow-sm">
                        {/* Edit Button */}
                        <button
                            onClick={() => onEditRequest(message.id)}
                            title="Edit Message"
                            className="p-1 rounded hover:bg-blue-700 text-white"
                            aria-label="Edit message"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                        </button>
                        {/* Copy Button */}
                        <button
                            onClick={() => onCopyResponse(message.content)}
                            title="Copy Message"
                            className="p-1 rounded hover:bg-blue-700 text-white"
                            aria-label="Copy message"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                        </button>
                    </div>
                )}

                {/* Action Buttons for Assistant Messages (not streaming) */}
                {message.role === 'assistant' && !isStreaming && !message.isThinking && !isSearching && (
                    <div className="absolute -bottom-2 right-2 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-200 dark:bg-gray-700 p-1 rounded-md shadow-sm">
                        {/* Regenerate Button */}
                        <button
                            onClick={() => onRegenerateRequest(message.id)}
                            title="Regenerate Response"
                            className="p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
                            aria-label="Regenerate response"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21.5 2v6h-6M2.5 22v-6h6" />
                                <path d="M22 11.5A10 10 0 0 0 3.5 12.5" />
                                <path d="M2 12.5a10 10 0 0 0 18.5-1" />
                            </svg>
                        </button>
                        {/* Copy Button */}
                        <button
                            onClick={() => onCopyResponse(message.content)}
                            title="Copy Response"
                            className="p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
                            aria-label="Copy response"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatMessage; 